<template>
  <div id="page-about" class="px-4 pb-6">
    <intro-tabs value="about" />
    <section class="key-vision h-70dvh lg:h-30dvw flex items-center -mx-4">
      <div class="content flex flex-col items-center justify-center mx-auto text-white">
        <nd-img src="@/assets/images/logo.svg" alt="Niceday Logo" class="w-224px h-96px" />
        <p class="my-4 text-center text-sm lg:text-base">{{ $t('about_us_slogan_2') }}</p>
      </div>
    </section>
    <section class="about-us container mx-auto my-6">
      <h2 class="heading">{{ $t('about_us_page_title') }}</h2>
      <div class="content flex flex-col-reverse gap-6 lg:flex-row lg:gap-20">
        <div class="image-wrapper">
          <nd-img src="/images/about/team.png" alt="Team Member" class="object-contain w-full h-full" />
        </div>
        <dl class="brief lg:max-w-2/5">
          <dt>{{ $t('about_us_platform_intro_title') }}</dt>
          <dd>{{ $t('about_us_platform_intro_desc') }}</dd>

          <dt>{{ $t('about_us_parent_needs_title') }}</dt>
          <dd>{{ $t('about_us_parent_needs_desc') }}</dd>

          <dt>{{ $t('about_us_slogan_1') }}<br />{{ $t('about_us_slogan_2') }}</dt>
        </dl>
      </div>
    </section>
    <section class="our-visions container mx-auto my-6">
      <h3 class="heading">{{ $t('about_us_corp_vision') }}</h3>
      <div class="content grid grid-cols-1 lg:grid-cols-2 gap-x-64px gap-y-60px">
        <div v-for="v in OUR_VISIONS_CONTENT" :key="v.title" class="vision">
          <div class="image-wrapper">
            <nd-img
              :src="`/images/about/${v.img}@2x.png`"
              :srcset="`/images/about/${v.img}.png 500w`"
              :alt="v.title"
              class="object-contain w-full h-full max-h-210px lg:max-h-unset"
            />
          </div>
          <dl class="brief">
            <dt>{{ v.title }}</dt>
            <dd>{{ v.content }}</dd>
          </dl>
        </div>
      </div>
    </section>
    <div v-if="locale === LangEnum.TW" class="divider container mx-auto mt-60px lg:mt-120px">
      <hr />
    </div>
    <section v-if="locale === LangEnum.TW" id="media" class="press container mx-auto pt-1 -mt-1 mb-6">
      <h3 class="heading">媒體報導</h3>
      <div class="contact-us mt-65px mb-90px text-center tracking-0.5px">
        <span class="text-black-30">媒體採訪聯繫：</span>
        <a class="text-yellow-50 hover:text-yellow-30 transition-colors" href="mailto:tw.pr@myfunnow.com">tw.pr@myfunnow.com</a>
      </div>
      <div class="press-list lg:grid-cols-3 grid grid-cols-1 gap-6">
        <div v-for="press in PRESS_LIST" :key="press.link" class="press-card pt-9 pb-30px px-6 border border-gray-30 tracking-0.5px">
          <div class="image-wrapper w-140px mx-auto">
            <nd-img :src="press.img" :alt="press.media" class="w-full h-full" />
          </div>
          <p class="text-black-20 my-8 line-clamp-2">{{ press.desc }}</p>
          <div class="link">
            <a :href="press.link" target="_blank" rel="noopener noreferrer" class="text-black-40 hover:text-black-10 transition-colors">閱讀更多</a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import IntroTabs from '@/components/common/intro-tabs.vue'
import { LangEnum } from '@/i18n.config'

const { t, locale } = useI18n()

definePageMeta({
  middleware: ['check-region'],
})

const OUR_VISIONS_CONTENT = computed(() => [
  {
    img: 'idea1',
    title: t('about_us_corp_value_variety_title'),
    content: t('about_us_corp_value_variety_desc'),
  },
  {
    img: 'idea2',
    title: t('about_us_corp_value_convience_title'),
    content: t('about_us_corp_value_convience_desc'),
  },
  {
    img: 'idea3',
    title: t('about_us_corp_value_quality_title'),
    content: t('about_us_corp_value_quality_desc'),
  },
  {
    img: 'idea4',
    title: t('about_us_corp_value_supported_title'),
    content: t('about_us_corp_value_supported_desc'),
  },
])

// 僅在 zh-tw 顯示該區塊，故不需要 i18n。
const PRESS_LIST = [
  {
    img: '/images/about/media-money-udn.png',
    media: '經濟日報',
    desc: '暑假來臨不焦慮 Niceday 玩體驗推兒童未來力夏令營',
    link: 'https://money.udn.com/money/story/5612/6307269',
  },
  {
    img: '/images/about/media-business-next.png',
    media: '數位時代',
    desc: 'FunNow 宣布合併 Niceday，鎖定親子客群',
    link: 'https://www.bnext.com.tw/article/68792/funnow-niceday-2022',
  },
  {
    img: '/images/about/media-inside.png',
    media: 'Inside',
    desc: 'FunNow 併購 Niceday 特色體驗預訂平台，強化親子內容',
    link: 'https://www.inside.com.tw/article/27496-funnow-niceday',
  },
  {
    img: '/images/about/media-gvm.png',
    media: '遠見',
    desc: '顛覆旅遊想像！Niceday靠「體驗」打響知名度',
    link: 'https://www.gvm.com.tw/article/45789',
  },
  {
    img: '/images/about/media-udn.png',
    media: '聯合報',
    desc: '新創FunNow攻親子體驗市場 合併原富爾特旗下Niceday',
    link: 'https://udn.com/news/story/7241/6268291',
  },
  {
    img: '/images/about/media-buzz-orange.png',
    media: 'BuzzOrange',
    desc: '專訪 Niceday：別再給我龍山寺 feat. 華西街了！台灣人要的是不一樣的旅遊體驗',
    link: 'https://buzzorange.com/techorange/2014/10/21/interview-niceday/',
  },
] as const
</script>

<style lang="scss" scoped>
#page-about {
  .key-vision {
    background: url(/images/about/banner_mobile.png) center center / cover no-repeat;
    @screen lg {
      background: url(/images/about/banner_desktop.png) center center / cover no-repeat;
    }
  }
  .heading {
    @apply flex justify-center items-center flex-col text-30px leading-47px text-center mb-50px mt-70px font-normal text-black-50;
    @apply lg:text-32px lg:mb-90px lg:mt-140px
    letter-spacing: 0.67px;

    &::after {
      @apply block bg-yellow-50 h-1 w-10 mt-14px;
      content: '';
    }
  }
  .brief {
    dt {
      @apply text-base leading-6 font-medium my-6 text-black-50 tracking-0.62px;
      @apply lg:text-xl lg:leading-30px lg:my-8;
    }
    dd {
      @apply text-sm lg:text-base text-black-20 tracking-0.5px;
    }
  }
}
</style>
